import React, { useEffect, useRef, useState } from "react";

export default function InteractiveOrb() {
  const canvasRef = useRef(null);
  const shapeRef = useRef(0);
  const rotationRef = useRef(0);
  const redrawRef = useRef(() => {});
  const [shape, setShape] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const timer = setInterval(() => {
      if (!paused && !reduced.matches && !document.hidden) setShape(value => (value + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, [paused]);
  useEffect(() => {
    shapeRef.current = shape;
    redrawRef.current();
  }, [shape]);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const root = document.documentElement;
    let frame,
      width = 0,
      height = 0,
      visible = true,
      rotation = rotationRef.current,
      last = 0;
    let orbFar = [86, 116, 153];
    let orbNear = [213, 229, 248];
    const updatePalette = () => {
      const styles = getComputedStyle(root);
      const far = styles.getPropertyValue("--orb-far").trim();
      const near = styles.getPropertyValue("--orb-near").trim();
      if (far) orbFar = far.split(",").map(Number);
      if (near) orbNear = near.split(",").map(Number);
    };
    updatePalette();
    let pointer = { x: 0, y: 0 },
      eased = { x: 0, y: 0 },
      morph = shapeRef.current;
    const count = 1050;
    const points = Array.from({ length: count }, (_, i) => {
      const y = 1 - (i / (count - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const angle = i * Math.PI * (3 - Math.sqrt(5));
      return { x: Math.cos(angle) * r, y, z: Math.sin(angle) * r, angle, i };
    });
    function draw(time = 0) {
      const dt = Math.min((time - last) / 1000 || 0, 0.04);
      last = time;
      const moving = !paused && !reduced.matches;
      if (moving) rotation += dt * 0.13;
      rotationRef.current = rotation;
      eased.x += (pointer.x - eased.x) * 0.06;
      eased.y += (pointer.y - eased.y) * 0.06;
      morph += (shapeRef.current - morph) * (moving ? 0.035 : 1);
      ctx.clearRect(0, 0, width, height);
      const radius = Math.min(width, height) * 0.35;
      const ry = rotation + (moving ? eased.x * 0.35 : 0),
        rx = -0.2 + (moving ? eased.y * 0.2 : 0);
      const transformed = points
        .map((p) => {
          const wave = 1 + Math.sin(p.y * 6 + rotation * 3) * 0.075;
          const torusWeight = Math.max(0, 1 - Math.abs(morph - 1));
          const discWeight = Math.max(0, morph - 1);
          const a = (p.i / count) * Math.PI * 24;
          const b = p.angle;
          const tx = (0.72 + 0.28 * Math.cos(b)) * Math.cos(a);
          const ty = 0.28 * Math.sin(b);
          const tz = (0.72 + 0.28 * Math.cos(b)) * Math.sin(a);
          let x = p.x * wave * (1 - torusWeight) + tx * torusWeight;
          let y = p.y * wave * (1 - torusWeight) + ty * torusWeight;
          let z = p.z * wave * (1 - torusWeight) + tz * torusWeight;
          y *= 1 - discWeight * 0.65;
          x *= 1 + discWeight * 0.13;
          const xx = x * Math.cos(ry) + z * Math.sin(ry);
          const zz = -x * Math.sin(ry) + z * Math.cos(ry);
          const yy = y * Math.cos(rx) - zz * Math.sin(rx);
          const depth = y * Math.sin(rx) + zz * Math.cos(rx);
          const perspective = 3 / (3 - depth * 0.45);
          return {
            x: width / 2 + xx * radius * perspective,
            y: height / 2 + yy * radius * perspective,
            z: depth,
          };
        })
        .sort((a, b) => a.z - b.z);
      for (const p of transformed) {
        const front = (p.z + 1.4) / 2.8;
        const red = Math.round(orbFar[0] + (orbNear[0] - orbFar[0]) * front);
        const green = Math.round(orbFar[1] + (orbNear[1] - orbFar[1]) * front);
        const blue = Math.round(orbFar[2] + (orbNear[2] - orbFar[2]) * front);
        ctx.fillStyle = `rgba(${red},${green},${blue},${0.18 + front * 0.72})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 0.5 + front * 1.05, 0, Math.PI * 2);
        ctx.fill();
      }
      if (visible && !document.hidden && moving)
        frame = requestAnimationFrame(draw);
    }
    const restart = () => {
      cancelAnimationFrame(frame);
      last = 0;
      if (visible && !document.hidden) frame = requestAnimationFrame(draw);
    };
    redrawRef.current = restart;
    const resize = new ResizeObserver(([entry]) => {
      width = entry.contentRect.width;
      height = entry.contentRect.height;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      restart();
    });
    resize.observe(canvas);
    const themeObserver = new MutationObserver(() => {
      updatePalette();
      restart();
    });
    themeObserver.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      restart();
    });
    observer.observe(canvas);
    const move = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer = {
        x: (e.clientX - rect.left) / rect.width - 0.5,
        y: (e.clientY - rect.top) / rect.height - 0.5,
      };
    };
    const leave = () => {
      pointer = { x: 0, y: 0 };
    };
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerleave", leave);
    reduced.addEventListener("change", restart);
    document.addEventListener("visibilitychange", restart);
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      themeObserver.disconnect();
      observer.disconnect();
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerleave", leave);
      reduced.removeEventListener("change", restart);
      document.removeEventListener("visibilitychange", restart);
    };
  }, [paused]);
  return (
    <div className="orb-experience" data-shape={shape}>
      <div className="orb-stage">
        <div className="orb-halo" />
        <canvas ref={canvasRef} aria-hidden="true" />
        

      </div>
      <div className="orb-controls" role="group" aria-label="오브 형태 선택">
        {["정부사업", "기업", "소상공인"].map((label, i) => (
          <button
            key={label}
            aria-pressed={shape === i}
            onClick={() => setShape(i)}
          >
            <span className="dot" />
            {label}
          </button>
        ))}
        <button
          className="orb-pause"
          onClick={() => setPaused(!paused)}
          aria-label={paused ? "오브 움직임 재생" : "오브 움직임 정지"}
        >
          {paused ? "재생" : "정지"}
        </button>
      </div>
    </div>
  );
}
